import { useAddGraphicSiteMutation, useAddGraphicVersionMutation, useAddSiteMutation, useLazyGetItemDetailsQuery } from "api/collaborateApi";
import { useGetSitesAcrossWorkspacesQuery, useGetSitesQuery, useLazyGetSitesQuery } from "api/siteApi";
import { useGetWorkspacesQuery } from "api/workspaceApi";
import { Check, ChevronDown, CircleHelp, InboxIcon, Monitor, PanelTop, PlusCircle } from "lucide-react";
import brokenImage from 'media/images/broken-image.png';
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GetItemDetailsGraphic } from "types/api/collaborate";
import { SiteAcrossWorkspaces } from "types/api/site";
import { WorkspacesResponse } from "types/api/workspaces";
import { AnimatedLoader, Avatar, Badge, Button, Command, Dialog, Input, Popover, ResponsiveDialog, Select, Separator, Tooltip } from "ui";
import { TooltipTrigger } from "ui/components/Tooltip";
import { cn } from "utils/cn";
import { getHostAndPath, isInChromeExtension } from "utils/proxyUtils";
import { ScreenCapture } from "../../ScreenCapture";
import { getSelectedSiteInfo, getShowSiteSelectionModal, SelectedSiteInfo, selectSite, setShowSiteSelectionModal } from "../../store/siteInfoSlice";
import { clearPendingTask, setCreateModalOpen, setSelectedTaskId } from "../../store/tasksSlice";
import { collaborateUrl } from "../../utils/collaborateUrl";

export const SiteSelectionModal = () => {
    const dispatch = useDispatch();
    const showSiteSelectionModal = useSelector(getShowSiteSelectionModal);
    const { data: workspaces, isLoading: isLoadingWorkspaces } = useGetWorkspacesQuery();
    const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspacesResponse['data'][0] | null>(null);
    const [addSite, { isLoading: isAddingSite }] = useAddSiteMutation();
    const [getSites] = useLazyGetSitesQuery();
    const [selectedProject, setSelectedProject] = useState<string>('');
    const [selectedGraphic, setSelectedGraphic] = useState<string>('');
    const [graphicOpen, setGraphicOpen] = useState(false);
    const [newGraphicSiteName, setNewGraphicSiteName] = useState(document.title || 'New Graphic Project');
    const [getItemDetails] = useLazyGetItemDetailsQuery();
    const [addGraphicSite, { isLoading: isAddingGraphicSite }] = useAddGraphicSiteMutation();
    const [addGraphicVersion, { isLoading: isAddingGraphicVersion }] = useAddGraphicVersionMutation();
    const [isCapturingViewport, setIsCapturingViewport] = useState(false);
    const [isCapturingFullPage, setIsCapturingFullPage] = useState(false);
    const [graphicPages, setGraphicPages] = useState<GetItemDetailsGraphic[]>([]);
    const screenCaptureRef = useRef<ScreenCapture | null>(null);
    const [siteName, setSiteName] = useState(document.title);
    const [isLoadingProject, setIsLoadingProject] = useState(false); // New loading state
    const host = getHostAndPath(collaborateUrl())?.host;

    const { data: graphicSites, isFetching: isFetchingGraphics } = useGetSitesQuery({
        workspace_id: selectedWorkspace?.id.toString() || '',
        filter_by: 'graphic',
    }, {
        skip: !selectedWorkspace
    });

    const { currentData: sitesAcrossWorkspaces, isLoading } = useGetSitesAcrossWorkspacesQuery({
        host: host ? host : '',
    }, {
        skip: !host
    });

    // Initialize ScreenCapture instance
    useEffect(() => {
        screenCaptureRef.current = new ScreenCapture();

        // Cleanup on unmount
        return () => {
            if (screenCaptureRef.current) {
                screenCaptureRef.current.restoreElementStates();
            }
        };
    }, []);

    // Helper function to convert base64 to File
    const base64ToFile = (base64String: string, filename: string): File => {
        const arr = base64String.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    };

    // Helper function to handle graphic creation/update
    const handleGraphicUpload = async (screenshot: string) => {
        try {
            if (selectedProject === 'new') {
                // Create new graphic site
                const { data } = await addGraphicSite({
                    title: newGraphicSiteName,
                    image: screenshot,
                    // No site_id for new projects
                });

                if (data && data.id) {
                    const url = new URL(import.meta.env.VITE_APP_ATARIM_URL + `/collaborate`);
                    url.searchParams.set('item', data.id.toString());
                    url.searchParams.set('graphicid', data.graphics[data.graphics.length - 1].id.toString() || '');
                    window.open(url.toString(), '_blank');
                }

                return data;
            } else if (selectedGraphic) {
                // Add version to existing graphic
                const imageFile = base64ToFile(screenshot, `screenshot-${Date.now()}.png`);

                const { data } = await addGraphicVersion({
                    graphicId: selectedGraphic,
                    imageFile: imageFile,
                });

                if (data && data.id) {
                    const url = new URL(import.meta.env.VITE_APP_ATARIM_URL + `/collaborate`);
                    url.searchParams.set('item', selectedProject);
                    url.searchParams.set('graphicid', data.id.toString());
                    // open in new tab
                    window.open(url.toString(), '_blank');
                }

                return data;
            } else {
                // Selected project but no graphic - create new graphic with site_id
                const { data } = await addGraphicSite({
                    title: `Screenshot - ${new Date().toLocaleString()}`,
                    image: screenshot,
                    site_id: selectedProject,
                });

                if (data && data.id) {
                    const url = new URL(import.meta.env.VITE_APP_ATARIM_URL + `/collaborate`);
                    url.searchParams.set('item', data.id.toString());
                    url.searchParams.set('graphicid', data.graphics[data.graphics.length - 1].id.toString() || '');
                    // open in new tab
                    window.open(url.toString(), '_blank');
                }

                return data;
            }
        } catch (error) {
            console.error('Failed to upload graphic:', error);
            throw error;
        }
    };

    const handleModalChange = (open: boolean) => {
        dispatch(setShowSiteSelectionModal(open));
    };

    const handleAddSite = async () => {
        if (selectedWorkspace) {
            // create a url for the current window without params
            const urlToAdd = window.location.href.split('?')[0];
            await addSite({ workspace_id: selectedWorkspace.id, url: urlToAdd, title: siteName }).then(
                res => {
                    if (res.data) {
                        getSites(
                            { host: urlToAdd, workspace_id: selectedWorkspace.id.toString() },
                            true
                        ).unwrap().then((res) => {
                            if (res.data && res.data.length > 0) {
                                const site = res.data[0];
                                const selectedSiteInfo: SelectedSiteInfo = {
                                    id: site.id,
                                    name: site.name,
                                    users: site.users,
                                    setting: site.setting,
                                    workspace_id: selectedWorkspace.id
                                };
                                dispatch(selectSite(selectedSiteInfo));
                            }
                        }).catch(err => {
                            console.error('Error fetching sites', err);
                        });
                        if (res.data.result.id) {
                            getItemDetails({ id: res.data.result.id, proxyMethod: 'injectable_script' }, true)
                                .unwrap()
                                .then((res) => {
                                    if (res.result) {
                                        dispatch(setShowSiteSelectionModal(false));
                                    }
                                }).catch(err => {
                                    console.error('Error fetching item details', err);
                                });
                        }
                    }
                }).catch(err => {
                    console.error('Error adding site', err);
                });
        }
    };

    const handleViewportScreenshot = async () => {
        if (!screenCaptureRef.current || !selectedWorkspace) return;

        try {
            setIsCapturingViewport(true);

            // Capture viewport screenshot
            const screenshot = await screenCaptureRef.current.capture({
                quality: 0.9,
                fullPage: false,
                useChromeApi: true,
                useBrowser: true,
                preserveElements: false,
            });

            // Upload the screenshot
            await handleGraphicUpload(screenshot);

            // Close modal on success
            dispatch(setShowSiteSelectionModal(false));

        } catch (error) {
            console.error('Failed to capture or upload viewport screenshot:', error);
            // You might want to show a toast notification here
        } finally {
            setIsCapturingViewport(false);
        }
    };

    const handleFullPageScreenshot = async () => {
        if (!screenCaptureRef.current || !selectedWorkspace) return;

        try {
            setIsCapturingFullPage(true);

            // Capture full page screenshot
            const screenshot = await screenCaptureRef.current.capture({
                quality: 0.85,
                fullPage: true,
                preserveElements: true,
                useChromeApi: true,
                useHtml2Canvas: false,
                useBrowser: false,
            });

            // Upload the screenshot
            await handleGraphicUpload(screenshot);

            // Close modal on success
            dispatch(setShowSiteSelectionModal(false));

        } catch (error) {
            console.error('Failed to capture full page screenshot:', error);

            // Fallback to viewport capture
            try {
                const fallbackScreenshot = await screenCaptureRef.current.capture({
                    quality: 0.85,
                    fullPage: false,
                    useChromeApi: true,
                    useBrowser: true,
                });

                // Upload the fallback screenshot
                await handleGraphicUpload(fallbackScreenshot);

                // Close modal on success
                dispatch(setShowSiteSelectionModal(false));

            } catch (fallbackError) {
                console.error('Fallback capture also failed:', fallbackError);
            }
        } finally {
            setIsCapturingFullPage(false);
        }
    };

    useEffect(() => {
        //set a default workspace, if none is selected, lowest id workspace is selected
        if (workspaces && workspaces.data.length > 0) {
            const defaultWorkspace = workspaces.data.find(ws => ws.pivot.is_default) || workspaces.data[0];
            if (defaultWorkspace) {
                setSelectedWorkspace(defaultWorkspace);
            }
        }
    }, [workspaces]);

    useEffect(() => {
        async function fetchItemDetails() {
            if (selectedProject && selectedProject !== 'new') {
                try {
                    await getItemDetails({ id: selectedProject, proxyMethod: 'injectable_script' }, true).unwrap().then((res) => {
                        setGraphicPages(res.result.graphics || []);
                    }).catch(err => {
                        console.error('Error fetching graphic pages', err);
                    });
                } catch (err) {
                    console.error('Error fetching item details', err);
                }
            }
        }
        fetchItemDetails();
    }, [selectedProject, getItemDetails, dispatch]);

    // Calculate loading states
    const isUploading = isAddingGraphicSite || isAddingGraphicVersion;
    const isModalBlocked = isLoadingProject || isUploading || isCapturingViewport || isCapturingFullPage;

    return (
        <ResponsiveDialog
            open={!!showSiteSelectionModal}
            onOpenChange={handleModalChange}
        >
            <ResponsiveDialog.Content
                showClose={false}
                aria-describedby={undefined}
                className="max-w-3xl tall:max-h-[95vh] mh700:max-h-[100vh] mh700:h-full tall:h-min"
                container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
            >
                <div className="relative flex flex-col">
                    {/* Global loading overlay */}
                    {isModalBlocked && (
                        <div className="absolute inset-0 bg-background/80 backdrop-blur-[2px] z-50 flex items-center justify-center rounded-lg">
                            <div className="flex flex-col items-center gap-3">
                                <AnimatedLoader size={32} />
                                <div className="text-center">
                                    <h3 className="font-medium text-textcolor">
                                        {isLoadingProject && "Loading Project..."}
                                        {isCapturingViewport && "Capturing Viewport..."}
                                        {isCapturingFullPage && "Capturing Full Page..."}
                                        {isAddingGraphicSite && "Creating Graphic Project..."}
                                        {isAddingGraphicVersion && "Adding Screenshot..."}
                                    </h3>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Please wait while we process your request
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <Dialog.Title className="flex items-center gap-2 mt-2">
                        Project Select
                    </Dialog.Title>

                    {sitesAcrossWorkspaces && sitesAcrossWorkspaces.length > 0 ? (
                        <div className='flex flex-col gap-2 w-full'>
                            <p className="text-sm text-textcolor">Select an existing project to dive back in, add a new one with a URL, or start fresh with an image-based project for visual feedback. It all begins here.</p>
                            <div className="grid grid-cols-3 gap-4 px-1 py-2 max-h-96 overflow-y-auto"
                                onWheel={(e: React.WheelEvent) => {
                                    e.stopPropagation();
                                    const target = e.currentTarget;
                                    target.scrollTop += e.deltaY;
                                }}
                            >
                                {sitesAcrossWorkspaces.map(site => (
                                    <ProjectItem
                                        key={site.site_id}
                                        site={site}
                                        onLoadingChange={setIsLoadingProject} // Pass loading state setter
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <> {
                            isLoading ?
                                <div
                                    className={cn(
                                        'flex h-96 w-fit mx-auto flex-col items-center justify-center gap-1'
                                    )}
                                >
                                    <AnimatedLoader />
                                    <h2 className="text-textcolor/50">Loading Projects...</h2>
                                </div>
                                :
                                <div
                                    className={cn(
                                        'flex h-96 w-fit mx-auto flex-col items-center justify-center gap-1'
                                    )}
                                >
                                    <InboxIcon size={20} />
                                    <h2 className="font-medium">No Matching Projects Found</h2>
                                    <span className="text-center text-sm text-gray-500">
                                        Select a workspace below and then click the "Create Project" button to begin.
                                    </span>
                                </div>
                        }
                        </>
                    )}

                    <Separator className="my-4" />

                    <div className="flex flex-col justify-between w-full gap-4 grow">
                        <div className="flex gap-4 justify-between items-center">
                            <div className="flex flex-col justify-center">
                                <h3 className="font-bold text-sm text-textcolor">Select Workspace</h3>
                                <p className="text-sm text-textcolor">Choose where your new project lives</p>
                            </div>
                            <WorkspaceSelection
                                selectedWorkspace={selectedWorkspace}
                                setSelectedWorkspace={setSelectedWorkspace}
                                isLoading={isLoadingWorkspaces}
                                workspaces={workspaces}
                            />
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="flex flex-col border border-border rounded-lg p-4 w-full h-full">
                                <div className="flex items-center gap-2">
                                    <h3 className="font-bold text-sm text-textcolor">Live Site Feedback</h3>
                                    <CircleHelp size={14} className="text-textcolor" />
                                </div>
                                <p className="text-sm text-textcolor">Collaborate on the current web page instantly</p>
                                <Input className="mt-2"
                                    value={siteName}
                                    onChange={(e) => setSiteName(e.target.value)}
                                    placeholder="Project Name"
                                    autoFocus
                                />
                                <p className="text-xs text-textcolor/50 mt-1">Give your project a recognizable name</p>
                                <Button
                                    variant="muted"
                                    className="mt-auto"
                                    onClick={handleAddSite}
                                    disabled={!selectedWorkspace || isModalBlocked}
                                    loading={isAddingSite}
                                    rightIcon={<PlusCircle size={18} />}
                                >
                                    Create Project
                                </Button>
                            </div>

                            <div className="flex flex-col border border-border rounded-lg p-4 w-full h-full">
                                <div className="flex items-center gap-2">
                                    <h3 className="font-bold text-sm text-textcolor">Screenshot Feedback</h3>
                                    <CircleHelp size={14} className="text-textcolor" />
                                </div>
                                <p className="text-sm text-textcolor">Screenshot this page and assign to a project</p>
                                <div className="flex flex-col mb-2">
                                    <Select
                                        disabled={isFetchingGraphics || isModalBlocked}
                                        value={selectedProject}
                                        onValueChange={setSelectedProject}
                                        open={graphicOpen}
                                        onOpenChange={setGraphicOpen}
                                    >
                                        <Select.Trigger className="w-full mt-2">
                                            <Select.Value
                                                asChild={selectedProject === 'new'}
                                                placeholder={isFetchingGraphics ? 'Loading...' : 'Select a graphic project'}
                                            >
                                                <span className="text-sm text-textcolor">
                                                    {selectedProject === 'new' ? 'New Graphic Project' : graphicSites?.data.find(g => g.id.toString() === selectedProject)?.name || 'Select a graphic project'}
                                                </span>
                                            </Select.Value>
                                        </Select.Trigger>
                                        <Select.Content
                                            portal={true}
                                            container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}>
                                            <Button
                                                variant={"muted"}
                                                leftIcon={<PlusCircle size={16} />}
                                                className="w-full"
                                                onClick={() => {
                                                    setSelectedProject('new');
                                                    setGraphicOpen(false);
                                                }}
                                                disabled={!selectedWorkspace || isModalBlocked}
                                            >
                                                Create New Graphic Project
                                            </Button>
                                            {graphicSites &&
                                                graphicSites.data.map((graphicSite) => (
                                                    <Select.Item value={graphicSite.id.toString()} key={graphicSite.id}>
                                                        {graphicSite.name}
                                                    </Select.Item>
                                                ))}
                                        </Select.Content>
                                    </Select>
                                    {selectedProject === 'new' ? (
                                        <Input
                                            className="mt-2"
                                            value={newGraphicSiteName}
                                            onChange={(e) => setNewGraphicSiteName(e.target.value)}
                                            placeholder={document.title || 'New Graphic Project Name'}
                                            disabled={isModalBlocked}
                                        />
                                    ) :
                                        <Select
                                            disabled={graphicPages.length === 0 || isFetchingGraphics || isModalBlocked}
                                            value={selectedGraphic}
                                            onValueChange={setSelectedGraphic}
                                        >
                                            <Select.Trigger className="w-full mt-2">
                                                <Select.Value
                                                    placeholder={isFetchingGraphics ? 'Loading...' : 'Select a graphic design'}
                                                />
                                            </Select.Trigger>
                                            <Select.Content
                                                portal={true}
                                                container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}>                                                {graphicPages &&
                                                    graphicPages.map((graphic) => (
                                                        <Select.Item value={graphic.id.toString()} key={graphic.id}>
                                                            {graphic.title}
                                                        </Select.Item>
                                                    ))}
                                            </Select.Content>
                                        </Select>
                                    }
                                </div>
                                <div className="flex items-center justify-between mt-auto gap-4">
                                    <Button
                                        variant="muted"
                                        className="w-full"
                                        onClick={handleViewportScreenshot}
                                        disabled={!selectedWorkspace || isModalBlocked}
                                        loading={isCapturingViewport}
                                        rightIcon={<Monitor size={18} />}
                                    >
                                        Your View
                                    </Button>
                                    <Button
                                        variant="muted"
                                        className="w-full"
                                        onClick={handleFullPageScreenshot}
                                        disabled={!selectedWorkspace || isModalBlocked}
                                        loading={isCapturingFullPage}
                                        rightIcon={<PanelTop size={18} />}
                                    >
                                        Full Page
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div></div>
            </ResponsiveDialog.Content>
        </ResponsiveDialog>
    );
};

function WorkspaceSelection({
    selectedWorkspace,
    setSelectedWorkspace,
    isLoading,
    workspaces
}: {
    selectedWorkspace: WorkspacesResponse['data'][0] | null
    setSelectedWorkspace: (workspace: WorkspacesResponse['data'][0]) => void
    isLoading: boolean
    workspaces?: WorkspacesResponse
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const renderRightIcon = () => {
        if (isLoading) {
            return <AnimatedLoader size={20} />;
        }
        return <ChevronDown className="h-4 w-4" />;
    };

    const renderSelectedItem = () => {
        if (isLoading) {
            return <div>Loading...</div>;
        }

        if (!selectedWorkspace) {
            return <div>Select a workspace</div>;
        }

        return (
            <WorkspaceItem workspace={selectedWorkspace} />
        );
    };

    return (
        <Popover modal={false} open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    rightIcon={renderRightIcon()}
                    className="w-[250px] hover:bg-tertiary/5 h-12 justify-between p-1"
                    disabled={isLoading || !workspaces}
                >
                    {renderSelectedItem()}
                </Button>
            </Popover.Trigger>
            <Popover.Content className="relative w-screen p-0 md:w-72"
                portal={true}
                container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
            >
                <Command value={selectedWorkspace?.id.toString()}>
                    <Command.Input
                        placeholder="Search workspaces..."
                        value={search}
                        onValueChange={setSearch}
                    />
                    <Command.Empty>No workspace found.</Command.Empty>
                    <Command.List className="max-h-[300px] max-w-80">
                        <Command.Group>
                            {workspaces && workspaces.data?.map((workspace) => (
                                <Command.Item
                                    key={workspace.id}
                                    value={workspace.id.toString()}
                                    keywords={[workspace.id.toString(), workspace.name]}
                                    onSelect={(workspaceId) => {
                                        const selected = workspaces.data.find(
                                            (ws) => ws.id.toString() === workspaceId
                                        );
                                        if (selected) {
                                            setSelectedWorkspace(selected);
                                        }
                                        setOpen(false);
                                    }}
                                    className={cn(
                                        'py-1',
                                        selectedWorkspace?.id.toString() === workspace.id.toString()
                                            ? 'bg-tertiary/5'
                                            : ''
                                    )}
                                >
                                    <WorkspaceItem workspace={workspace} />
                                    <Check
                                        className={cn(
                                            'aspect-square h-4 w-4',
                                            selectedWorkspace?.id.toString() === workspace.id.toString()
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        )}
                                    />
                                </Command.Item>
                            ))}
                        </Command.Group>
                    </Command.List>
                </Command>
                {isLoading && (
                    <div className="absolute bottom-0 z-50 mx-auto flex h-8 w-full items-center justify-center gap-2 rounded-xl bg-background/50 backdrop-blur-[2px]">
                        <AnimatedLoader size={20} />
                        Loading Workspaces...
                    </div>
                )}
            </Popover.Content>
        </Popover>
    );
}

function WorkspaceItem({ workspace }: { workspace: WorkspacesResponse['data'][0] }) {
    return (
        <div
            key={workspace.id}
            className='flex gap-2 items-center w-full overflow-hidden'>
            <Avatar
                src={workspace?.icon}
                alt={workspace?.name}
                id={workspace?.id}
                title={workspace?.name}
                size="md"
            />
            <div className='flex flex-col grow min-w-0 text-left'>
                <div className='font-medium text-sm truncate'>{workspace.name}</div>
                <div className='text-xs text-textcolor/50 truncate'>{workspace.description}</div>
            </div>
        </div>
    )
}

function ProjectItem({
    site,
    onLoadingChange
}: {
    site: SiteAcrossWorkspaces;
    onLoadingChange: (loading: boolean) => void;
}) {
    const dispatch = useDispatch();
    const imageRef = useRef<HTMLImageElement | null>(null);
    const [getItemDetails] = useLazyGetItemDetailsQuery();
    const [imageLoadFailed, setImageLoadFailed] = useState(false);
    const currentSelectedSite = useSelector(getSelectedSiteInfo);

    const handleImageError = () => {
        if (imageRef.current && !imageLoadFailed) {
            setImageLoadFailed(true);
            imageRef.current.src = brokenImage;
        }
    };

    const handleSiteClick = async () => {
        try {
            onLoadingChange(true); // Start global loading

            const selectedSiteInfo: SelectedSiteInfo = {
                id: site.site_id,
                name: site.name,
                users: [],
                setting: {
                    whiteLabel: {
                        favicon: site.favicon ?? '',
                        logo_link: null,
                    }
                },
                workspace_id: site.workspace_id
            };

            dispatch(selectSite(selectedSiteInfo));
            dispatch(clearPendingTask());
            dispatch(setSelectedTaskId(null));
            dispatch(setCreateModalOpen(false));

            if (isInChromeExtension()) {
                // If in Chrome extension, update the selected site in the background script
                chrome.runtime.sendMessage({
                    type: 'UPDATE_SELECTED_SITE',
                    payload: {
                        url: new URL(site.url).hostname,
                        siteId: site.site_id,
                        workspaceId: site.workspace_id,
                    }
                });
            }

            const res = await getItemDetails({
                id: site.site_id,
                proxyMethod: 'injectable_script'
            }, true).unwrap();

            if (res.result) {
                dispatch(setShowSiteSelectionModal(false));
            }
        } catch (err) {
            console.error('Error fetching item details', err);
        } finally {
            onLoadingChange(false); // Stop global loading
        }
    };

    return (
        <div
            className={cn(
                currentSelectedSite?.id === site.site_id
                    ? 'ring-2 ring-ata-purple hover:shadow-sm'
                    : 'hover:ring-2 hover:ring-primary/50 hover:shadow-sm',
                'flex flex-col border border-border rounded-lg transition-all cursor-pointer overflow-hidden'
            )}
            onClick={handleSiteClick}
        >
            <div className="w-full aspect-video overflow-hidden rounded-t-lg">
                {imageLoadFailed ? (
                    <div className="w-full h-full bg-border flex items-center justify-center">
                        <span className="text-gray-500 text-sm">Image not available</span>
                    </div>
                ) : (
                    <img
                        src={site.image ?? brokenImage}
                        ref={imageRef}
                        onError={handleImageError}
                        alt={site.name}
                        className="w-full h-full object-cover"
                    />
                )}
            </div>
            <div className='flex justify-between items-center p-2'>
                <div className='flex flex-col text-xs text-gray-500 min-w-0 flex-1'>
                    <span className="truncate">
                        {site.url_host} | {site.name}
                    </span>
                    <span className="truncate">Workspace: {site.workspace_name}</span>
                </div>
                <Tooltip content={"Tasks"} portal={true}>
                    <TooltipTrigger>
                        <Badge variant="tertiary" className="text-xs ml-2 flex-shrink-0">
                            {site.tasks_count}
                        </Badge>
                    </TooltipTrigger>
                </Tooltip>
            </div>
        </div>
    );
}